/* eslint-disable no-param-reassign */
import {
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Switch,
} from "@jhool-io/fe-components";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import BillingSummaryCard from "../BillingSummaryCard/BillingSummaryCard";
import {
    cn,
    isSmallScreen,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import TrendingUpIcon from "../../../../components/Icons/TrendingUp";
import TrendingDownIcon from "../../../../components/Icons/TrendingDown";
import BillingSummaryBreakdown from "../BillingSummaryBreakdown/BillingSummaryBreakdown";
import { useFetchBillingAdminDashboardList } from "../../../../hooks/queries/billing";

import ListState from "../../../../components/ListState/ListState";
import { IBillingDashboardMonthlyBreakdown } from "../../../../utils/types/billing";

import {
    billingPaymentMonths,
    BillingSummaryCardsFilters,
    getTimeRangeFromFilterValue,
} from "../../helpers/billing.helpers";
import Loader from "../../../../shared-ui/Loader/Loader";
import InsuranceProviderFilter, {
    getInsuranceProviderIdsFilter,
    setInsuranceProviderIdsFilter,
} from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";

export default function BillingSummaryWrapper() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [openDialog, setOpenDialog] = React.useState(false);

    const filter = searchParams.get("filter") || BillingSummaryCardsFilters[0];
    const insuranceProviderIdsFilter =
        getInsuranceProviderIdsFilter(searchParams);
    const typeFilter = searchParams.get("type") || "coinsurance";

    const { fromDate } = getTimeRangeFromFilterValue(filter);
    const { toDate } = getTimeRangeFromFilterValue(filter);

    const tableInView = searchParams.get("view") || "";

    const handleSwitchButtonClick = (key: string) => {
        searchParams.set("type", key);
        localStorage.setItem("billingsummary_filters", searchParams.toString());
        setInsuranceProviderIdsFilter(searchParams, []);
        setSearchParams(searchParams);
    };

    const { data, error, isLoading, isSuccess } =
        useFetchBillingAdminDashboardList(
            {
                type: insuranceProviderIdsFilter.includes("self_pay")
                    ? undefined
                    : typeFilter,
                show_invoice_breakdown: false,
                from_date: fromDate,
                to_date: toDate,
                insurance_provider_ids: insuranceProviderIdsFilter,
            },
            !tableInView
        );

    const switchOptions = [
        {
            title: "Co-insurance",
            key: "coinsurance",
            onClick: () => handleSwitchButtonClick("coinsurance"),
        },
        {
            title: "Insurance",
            key: "insurance",
            onClick: () => handleSwitchButtonClick("insurance"),
        },
    ];

    // Get percenatage difference between two numbers
    const getPercentageDifference = (prev: number, next: number) => {
        if (prev === 0) return <span>--%</span>;

        const diff = Math.abs(prev - next);

        const average = diff / prev;

        return (
            <span
                className={cn("text-textLink", {
                    "text-danger": prev > next,
                })}
            >
                {Math.abs(Math.round(average * 100))}%
            </span>
        );
    };

    // Get encounters amount to display
    const handleGetTotalEncounters = (
        monthtlyBreakdown: IBillingDashboardMonthlyBreakdown[]
    ) => {
        if (monthtlyBreakdown.length === 0) return 0;

        return monthtlyBreakdown
            .map((value) => value.encounters)
            .reduce(
                (accumulator, currentValue) => accumulator + currentValue,
                0
            );
    };

    const handleSetFilterValue = (value: string | number) => {
        searchParams.set("filter", String(value));
        localStorage.setItem("billingsummary_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleInsuranceProviderIdsChange = (
        insuranceProviderIds: string[]
    ) => {
        setInsuranceProviderIdsFilter(searchParams, insuranceProviderIds);
        localStorage.setItem("billingsummary_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleMonthClicked = (
        view: string,
        month: string,
        amount: number,
        count: number,
        credit?: number,
        refund?: number
    ) => {
        searchParams.set("view", view);
        searchParams.set("month", month);
        searchParams.set("amount", String(amount) || "");
        searchParams.set("count", String(count) || "");
        if (credit) {
            searchParams.set("credit", String(credit));
        }
        if (refund) {
            searchParams.set("refund", String(refund));
        }
        setOpenDialog(true);
        setSearchParams(searchParams);
    };

    const closeBreakdownDialog = () => {
        searchParams.delete("month");
        searchParams.delete("view");
        searchParams.delete("amount");
        searchParams.delete("count");
        searchParams.delete("credit");
        searchParams.delete("refund");
        searchParams.delete("provider");
        searchParams.delete("client");
        setOpenDialog(false);
        setSearchParams(searchParams);
    };

    const handleGetMonthlyBreakdown = () => {
        if (data?.data) {
            const sortMonthlyBreakdownDescending = data?.data.monthly_breakdown
                .sort((a, b) => b.payment_month - a.payment_month)
                .map((breakdown) => {
                    return {
                        ...breakdown,
                        amount_billed: breakdown.amount_billed,
                    };
                });

            return sortMonthlyBreakdownDescending;
        }

        return undefined;
    };

    // Check if breakdown list have data to display
    const breakdownList = handleGetMonthlyBreakdown();
    const doesBreakdownListHaveData = breakdownList && breakdownList.length > 0;

    return (
        <>
            <Dialog open={openDialog}>
                <DialogContent
                    handleCloseDialog={closeBreakdownDialog}
                    showFooter={false}
                    wrapperClass="w-[1186px]"
                    classNames="p-0"
                >
                    <BillingSummaryBreakdown />
                </DialogContent>
            </Dialog>
            <Card>
                <div className="flex items-center gap-[--spacing-items] flex-wrap justify-between">
                    <div className="flex flex-col gap-y-2">
                        <h2 className="font-extrabold text-title">
                            Billing Summary
                        </h2>
                        <p className="text-textSec font-normal text-base">
                            Get a quick overview of your billing activity and
                            performance
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-[--spacing-items]">
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton className="self-center">
                                    {makeStringFirstLetterCapital(
                                        removeEnumUnderscore(filter)
                                    )}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent
                                align={isSmallScreen() ? "start" : "end"}
                                width={200}
                            >
                                {[
                                    ...Object.values(
                                        BillingSummaryCardsFilters
                                    ),
                                ].map((item) => (
                                    <DropdownItem
                                        isSelected={filter === item}
                                        key={item}
                                        onClick={() =>
                                            handleSetFilterValue(item)
                                        }
                                    >
                                        {item === BillingSummaryCardsFilters[0]
                                            ? removeEnumUnderscore(item)
                                            : item}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>
                        <div className="self-center">
                            <InsuranceProviderFilter
                                values={insuranceProviderIdsFilter}
                                onChange={handleInsuranceProviderIdsChange}
                                alwaysShowSelfPay
                                isSelfPayExclusive
                                disabled={typeFilter === "coinsurance"}
                            />
                        </div>
                        <Switch
                            activeSwitch={typeFilter}
                            switchOptions={switchOptions}
                        />
                    </div>
                </div>

                {isLoading && <Loader wrapperClassName="h-[400px]" />}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error.response?.data.message ||
                            "Cannot display billing dashboard breakdown at this time please try again later"
                        }
                        cardClass="mt-16"
                    />
                )}
                {data && isSuccess && (
                    <div className="mt-[--spacing-header] grid gap-[--spacing-items] grid-cols-[repeat(auto-fit,minmax(400px,1fr))] md:grid-cols-[repeat(auto-fit,minmax(500px,1fr))] max-w-full overflow-x-auto">
                        <BillingSummaryCard
                            bgColor="var(--bg-encounter)"
                            title="Encounter"
                            tooltipDescription="Encounter shows the total number of sessions."
                            description="Total Encounter"
                            count={handleGetTotalEncounters(
                                data.data.monthly_breakdown
                            )}
                            showList={doesBreakdownListHaveData}
                            list={
                                <>
                                    <div className="rounded-md bg-[#F6E7EF] py-6 px-8 grid grid-cols-3 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                        <span className="capitalize">
                                            Month
                                        </span>
                                        <span>Total number</span>
                                        <span>Indicator</span>
                                    </div>
                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {handleGetMonthlyBreakdown()?.map(
                                            (item, i, arr) => (
                                                <span
                                                    key={`encounter - ${item.payment_month}`}
                                                    className="grid grid-cols-3 px-8 py-0 gap-x-2 rounded-none items-start justify-start text-caption text-left font-normal"
                                                >
                                                    <span className="capitalize">
                                                        {
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ]
                                                        }
                                                    </span>
                                                    <span>
                                                        {item.encounters}
                                                    </span>
                                                    <span className="flex items-center justify-start gap-x-1.5">
                                                        {arr[i]?.encounters ===
                                                            arr[i + 1]
                                                                ?.encounters &&
                                                            ""}
                                                        {arr[i]?.encounters >
                                                            arr[i + 1]
                                                                ?.encounters && (
                                                            <>
                                                                <TrendingUpIcon className="stroke-textLink" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.encounters,
                                                                    arr[i]
                                                                        ?.encounters
                                                                )}
                                                            </>
                                                        )}
                                                        {arr[i]?.encounters <
                                                            arr[i + 1]
                                                                ?.encounters && (
                                                            <>
                                                                <TrendingDownIcon className="stroke-danger" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.encounters,
                                                                    arr[i]
                                                                        ?.encounters
                                                                )}
                                                            </>
                                                        )}
                                                    </span>
                                                </span>
                                            )
                                        )}
                                    </div>
                                </>
                            }
                        />
                        <BillingSummaryCard
                            bgColor="var(--bg-billed)"
                            title="Billed"
                            tooltipDescription="Billed displays charges that have been submitted for billing."
                            description="Total Amount Billed"
                            count={showMoneyInAppFormat(
                                data.data.summary.amount_billed
                            )}
                            smallCount={`${
                                data.data.summary.amount_billed_count
                            } bill${
                                data.data.summary.amount_billed_count > 1
                                    ? "s"
                                    : ""
                            }`}
                            showList={doesBreakdownListHaveData}
                            list={
                                <>
                                    <div className="rounded-md bg-[#DFEFF3] py-6 px-8 grid grid-cols-4 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                        <span className="capitalize">
                                            Month
                                        </span>
                                        <span>Total amount</span>
                                        <span>Total bills</span>
                                        <span>Indicator</span>
                                    </div>
                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {handleGetMonthlyBreakdown()?.map(
                                            (item, i, arr) => (
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    key={`billed - ${item.payment_month}`}
                                                    className="grid grid-cols-4 px-8 py-0 gap-x-2 rounded-none items-start justify-start !text-caption text-text text-left font-normal hover:bg-[#DFEFF3]"
                                                    onClick={() =>
                                                        handleMonthClicked(
                                                            "amount_billed",
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ],

                                                            item.amount_billed,
                                                            item.amount_billed_count
                                                        )
                                                    }
                                                >
                                                    <span className="capitalize">
                                                        {
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ]
                                                        }
                                                    </span>
                                                    <span>
                                                        {showMoneyInAppFormat(
                                                            item.amount_billed
                                                        )}
                                                    </span>
                                                    <span>
                                                        {
                                                            item.amount_billed_count
                                                        }
                                                    </span>
                                                    <span className="flex items-center justify-start gap-x-1.5">
                                                        {arr[i]
                                                            ?.amount_billed ===
                                                            arr[i + 1]
                                                                ?.amount_billed &&
                                                            ""}
                                                        {arr[i]?.amount_billed >
                                                            arr[i + 1]
                                                                ?.amount_billed && (
                                                            <>
                                                                <TrendingUpIcon className="stroke-textLink" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.amount_billed,
                                                                    arr[i]
                                                                        ?.amount_billed
                                                                )}
                                                            </>
                                                        )}
                                                        {arr[i]?.amount_billed <
                                                            arr[i + 1]
                                                                ?.amount_billed && (
                                                            <>
                                                                <TrendingDownIcon className="stroke-danger" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.amount_billed,
                                                                    arr[i]
                                                                        ?.amount_billed
                                                                )}
                                                            </>
                                                        )}
                                                    </span>
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </>
                            }
                        />
                        <BillingSummaryCard
                            bgColor="var(--bg-collected)"
                            title="Collected"
                            tooltipDescription="Collected shows payments that have been successfully collected."
                            description="Total Amount Collected"
                            count={showMoneyInAppFormat(
                                data.data.summary.amount_collected
                            )}
                            smallCount={`${
                                data.data.summary.amount_collected_count
                            } payment${
                                data.data.summary.amount_collected_count > 1
                                    ? "s"
                                    : ""
                            } collected`}
                            showList={doesBreakdownListHaveData}
                            list={
                                <>
                                    <div className="rounded-md bg-[#E0E5F7] py-6 px-8 grid grid-cols-4 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                        <span className="capitalize">
                                            Month
                                        </span>
                                        <span>Total amount</span>
                                        <span className="text-wrap">
                                            Total collected payments
                                        </span>
                                        <span>Indicator</span>
                                    </div>
                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {handleGetMonthlyBreakdown()?.map(
                                            (item, i, arr) => (
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    key={`collected - ${item.payment_month}`}
                                                    className="grid grid-cols-4 px-8 py-0 gap-x-2 rounded-none items-start justify-start !text-caption text-text text-left font-normal"
                                                    onClick={() =>
                                                        handleMonthClicked(
                                                            "amount_collected",
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ],
                                                            item.amount_collected,
                                                            item.amount_collected_count,
                                                            item.total_credit,
                                                            item.total_refunds
                                                        )
                                                    }
                                                >
                                                    <span className="capitalize">
                                                        {
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ]
                                                        }
                                                    </span>
                                                    <span>
                                                        {showMoneyInAppFormat(
                                                            item.amount_collected
                                                        )}
                                                    </span>
                                                    <span>
                                                        {
                                                            item.amount_collected_count
                                                        }
                                                    </span>
                                                    <span className="flex items-center justify-start gap-x-1.5">
                                                        {arr[i]
                                                            ?.amount_collected ===
                                                            arr[i + 1]
                                                                ?.amount_collected &&
                                                            ""}
                                                        {arr[i]
                                                            ?.amount_collected >
                                                            arr[i + 1]
                                                                ?.amount_collected && (
                                                            <>
                                                                <TrendingUpIcon className="stroke-textLink" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.amount_collected,
                                                                    arr[i]
                                                                        ?.amount_collected
                                                                )}
                                                            </>
                                                        )}
                                                        {arr[i]
                                                            ?.amount_collected <
                                                            arr[i + 1]
                                                                ?.amount_collected && (
                                                            <>
                                                                <TrendingDownIcon className="stroke-danger" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.amount_collected,
                                                                    arr[i]
                                                                        ?.amount_collected
                                                                )}
                                                            </>
                                                        )}
                                                    </span>
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </>
                            }
                        />
                        <BillingSummaryCard
                            bgColor="var(--bg-aging)"
                            title="Aging"
                            tooltipDescription="Aging shows the aging status of outstanding invoices or claims."
                            description="Total Amount"
                            count={showMoneyInAppFormat(
                                data.data.summary.amount_outstanding
                            )}
                            smallCount={`${
                                data.data.summary.amount_outstanding_count
                            } bill${
                                data.data.summary.amount_outstanding_count > 1
                                    ? "s"
                                    : ""
                            }`}
                            showList={doesBreakdownListHaveData}
                            list={
                                <>
                                    <div className="rounded-md bg-[#DDE5E7] py-6 px-8 grid grid-cols-4 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                        <span className="capitalize">
                                            Month
                                        </span>
                                        <span>Total amount</span>
                                        <span>Total aged bills</span>
                                        <span>Indicator</span>
                                    </div>
                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {handleGetMonthlyBreakdown()?.map(
                                            (item, i, arr) => (
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    key={`aging - ${item.payment_month}`}
                                                    className="grid grid-cols-4 px-8 py-0 gap-x-2 rounded-none items-start justify-start !text-caption text-text text-left font-normal hover:bg-[#DDE5E7]"
                                                    onClick={() =>
                                                        handleMonthClicked(
                                                            "amount_outstanding",
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ],
                                                            item.amount_outstanding,
                                                            item.amount_outstanding_count
                                                        )
                                                    }
                                                >
                                                    <span className="capitalize">
                                                        {
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ]
                                                        }
                                                    </span>
                                                    <span>
                                                        {showMoneyInAppFormat(
                                                            item.amount_outstanding
                                                        )}
                                                    </span>
                                                    <span>
                                                        {
                                                            item.amount_outstanding_count
                                                        }
                                                    </span>
                                                    <span className="flex items-center justify-start gap-x-1.5">
                                                        {arr[i]
                                                            ?.amount_outstanding ===
                                                            arr[i + 1]
                                                                ?.amount_outstanding &&
                                                            ""}
                                                        {arr[i]
                                                            ?.amount_outstanding >
                                                            arr[i + 1]
                                                                ?.amount_outstanding && (
                                                            <>
                                                                <TrendingUpIcon className="stroke-textLink" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.amount_outstanding,
                                                                    arr[i]
                                                                        ?.amount_outstanding
                                                                )}
                                                            </>
                                                        )}
                                                        {arr[i]
                                                            ?.amount_outstanding <
                                                            arr[i + 1]
                                                                ?.amount_outstanding && (
                                                            <>
                                                                <TrendingDownIcon className="stroke-danger" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.amount_outstanding,
                                                                    arr[i]
                                                                        ?.amount_outstanding
                                                                )}
                                                            </>
                                                        )}
                                                    </span>
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </>
                            }
                        />
                        <BillingSummaryCard
                            bgColor="var(--bg-unmatched-payment)"
                            title="Unmatched Payment"
                            tooltipDescription="Unmatched payments shows payments with no matching claims or invoices."
                            description="Total Unmatched Payment"
                            count={showMoneyInAppFormat(
                                data.data.summary.amount_unmatched
                            )}
                            smallCount={`${data.data.summary.amount_unmatched_count} unmatched payment`}
                            showList={
                                data.data.unmatched_payment_monthly_stat &&
                                data.data.unmatched_payment_monthly_stat
                                    .length > 0
                            }
                            list={
                                <>
                                    <div className="rounded-md bg-[#EBE7DB] py-6 px-8 grid grid-cols-4 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                        <span className="capitalize">
                                            Month
                                        </span>
                                        <span>Total amount</span>
                                        <span>Total aged bills</span>
                                        <span>Indicator</span>
                                    </div>
                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {data.data.unmatched_payment_monthly_stat
                                            ?.sort(
                                                (a, b) =>
                                                    b.payment_month -
                                                    a.payment_month
                                            )
                                            .map((item, i, arr) => (
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    key={`unmatched amount - ${item.payment_month}`}
                                                    className="grid grid-cols-4 px-8 py-0 gap-x-2 rounded-none items-start justify-start !text-caption text-text text-left font-normal hover:bg-[#EBE7DB]"
                                                    onClick={() =>
                                                        handleMonthClicked(
                                                            "unmatched_payments",
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ],
                                                            item.unmatched_amount_collected,
                                                            item.unmatched_amount_collected_count
                                                        )
                                                    }
                                                >
                                                    <span className="capitalize">
                                                        {
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ]
                                                        }
                                                    </span>
                                                    <span>
                                                        {showMoneyInAppFormat(
                                                            item.unmatched_amount_collected
                                                        )}
                                                    </span>
                                                    <span>
                                                        {
                                                            item.unmatched_amount_collected_count
                                                        }
                                                    </span>
                                                    <span className="flex items-center justify-start gap-x-1.5">
                                                        {arr[i]
                                                            ?.unmatched_amount_collected ===
                                                            arr[i + 1]
                                                                ?.unmatched_amount_collected &&
                                                            ""}
                                                        {arr[i]
                                                            ?.unmatched_amount_collected >
                                                            arr[i + 1]
                                                                ?.unmatched_amount_collected && (
                                                            <>
                                                                <TrendingUpIcon className="stroke-textLink" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.unmatched_amount_collected,
                                                                    arr[i]
                                                                        ?.unmatched_amount_collected
                                                                )}
                                                            </>
                                                        )}
                                                        {arr[i]
                                                            ?.unmatched_amount_collected <
                                                            arr[i + 1]
                                                                ?.unmatched_amount_collected && (
                                                            <>
                                                                <TrendingDownIcon className="stroke-danger" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.unmatched_amount_collected,
                                                                    arr[i]
                                                                        ?.unmatched_amount_collected
                                                                )}
                                                            </>
                                                        )}
                                                    </span>
                                                </Button>
                                            ))}
                                    </div>
                                </>
                            }
                        />
                        <BillingSummaryCard
                            bgColor="var(--bg-submitted-claims)"
                            title="Submitted claims"
                            tooltipDescription="Submitted claims lists claims that have been submitted for processing."
                            description="Total Submitted Claims"
                            count={data.data.summary.submitted_claims_count}
                            smallCount={showMoneyInAppFormat(
                                data.data.summary.submitted_claims_amount
                            )}
                            hide={typeFilter === "coinsurance"}
                            showList={doesBreakdownListHaveData}
                            list={
                                <>
                                    <div className="rounded-md bg-[#EEDEDE] py-6 px-8 grid grid-cols-4 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                        <span className="capitalize">
                                            Month
                                        </span>
                                        <span>Total amount</span>
                                        <span>Total claims</span>
                                        <span>Indicator</span>
                                    </div>
                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {handleGetMonthlyBreakdown()?.map(
                                            (item, i, arr) => (
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    key={`submitted claims - ${item.payment_month}`}
                                                    className="grid grid-cols-4 px-8 py-0 gap-x-2 rounded-none items-start justify-start !text-caption text-text text-left font-normal hover:bg-[#EEDEDE]"
                                                    onClick={() =>
                                                        handleMonthClicked(
                                                            "submitted_claims",
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ],
                                                            item.submitted_claims_amount,
                                                            item.submitted_claims_count
                                                        )
                                                    }
                                                >
                                                    <span className="capitalize">
                                                        {
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ]
                                                        }
                                                    </span>
                                                    <span>
                                                        {showMoneyInAppFormat(
                                                            item.submitted_claims_amount
                                                        )}
                                                    </span>
                                                    <span>
                                                        {
                                                            item.submitted_claims_count
                                                        }
                                                    </span>
                                                    <span className="flex items-center justify-start gap-x-1.5">
                                                        {arr[i]
                                                            ?.submitted_claims_amount ===
                                                            arr[i + 1]
                                                                ?.submitted_claims_amount &&
                                                            ""}
                                                        {arr[i]
                                                            ?.submitted_claims_amount >
                                                            arr[i + 1]
                                                                ?.submitted_claims_amount && (
                                                            <>
                                                                <TrendingUpIcon className="stroke-textLink" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.submitted_claims_amount,
                                                                    arr[i]
                                                                        ?.submitted_claims_amount
                                                                )}
                                                            </>
                                                        )}
                                                        {arr[i]
                                                            ?.submitted_claims_amount <
                                                            arr[i + 1]
                                                                ?.submitted_claims_amount && (
                                                            <>
                                                                <TrendingDownIcon className="text-danger" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.submitted_claims_amount,
                                                                    arr[i]
                                                                        ?.submitted_claims_amount
                                                                )}
                                                            </>
                                                        )}
                                                    </span>
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </>
                            }
                        />
                        <BillingSummaryCard
                            bgColor="var(--bg-approved-claims)"
                            title="Approved claims"
                            tooltipDescription="Approved claims displays claims that have been approved for payment by insurance."
                            description="Total Approved Claims"
                            count={data.data.summary.approved_claims_count}
                            smallCount={showMoneyInAppFormat(
                                data.data.summary.approved_claims_amount
                            )}
                            hide={typeFilter === "coinsurance"}
                            list={
                                <>
                                    <div className="rounded-md bg-[#D9EAD8] py-6 px-8 grid grid-cols-4 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                        <span className="capitalize">
                                            Month
                                        </span>
                                        <span>Total amount</span>
                                        <span>Total claims</span>
                                        <span>Indicator</span>
                                    </div>
                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {handleGetMonthlyBreakdown()?.map(
                                            (item, i, arr) => (
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    key={`approved claims - ${item.payment_month}`}
                                                    className="grid grid-cols-4 px-8 py-0 gap-x-2 rounded-none items-start justify-start !text-caption text-text text-left font-normal hover:bg-[#D9EAD8]"
                                                    onClick={() =>
                                                        handleMonthClicked(
                                                            "approved_claims",
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ],
                                                            item.approved_claims_amount,
                                                            item.approved_claims_count
                                                        )
                                                    }
                                                >
                                                    <span className="capitalize text-left">
                                                        {
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ]
                                                        }
                                                    </span>
                                                    <span>
                                                        {showMoneyInAppFormat(
                                                            item.approved_claims_amount
                                                        )}
                                                    </span>
                                                    <span>
                                                        {
                                                            item.approved_claims_count
                                                        }
                                                    </span>
                                                    <span className="flex items-center justify-start gap-x-1.5">
                                                        {arr[i]
                                                            ?.approved_claims_amount ===
                                                            arr[i + 1]
                                                                ?.approved_claims_amount &&
                                                            ""}
                                                        {arr[i]
                                                            ?.approved_claims_amount >
                                                            arr[i + 1]
                                                                ?.approved_claims_amount && (
                                                            <>
                                                                <TrendingUpIcon className="stroke-textLink" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.approved_claims_amount,
                                                                    arr[i]
                                                                        ?.approved_claims_amount
                                                                )}
                                                            </>
                                                        )}
                                                        {arr[i]
                                                            ?.approved_claims_amount <
                                                            arr[i + 1]
                                                                ?.approved_claims_amount && (
                                                            <>
                                                                <TrendingDownIcon className="stroke-danger" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.approved_claims_amount,
                                                                    arr[i]
                                                                        ?.approved_claims_amount
                                                                )}
                                                            </>
                                                        )}
                                                    </span>
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </>
                            }
                        />
                        <BillingSummaryCard
                            bgColor="var(--bg-denied-claims)"
                            title="Denied claims"
                            tooltipDescription="Denied claims shows claims that have been denied by insurance."
                            description="Total Denied Claims"
                            count={data.data.summary.denied_claims_count}
                            smallCount={showMoneyInAppFormat(
                                data.data.summary.denied_claims_amount
                            )}
                            hide={typeFilter === "coinsurance"}
                            showList={doesBreakdownListHaveData}
                            list={
                                <>
                                    <div className="rounded-md bg-[#E8D7EE] py-6 px-8 grid grid-cols-4 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                        <span className="capitalize">
                                            Month
                                        </span>
                                        <span>Total amount</span>
                                        <span>Total claims</span>
                                        <span>Indicator</span>
                                    </div>
                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {handleGetMonthlyBreakdown()?.map(
                                            (item, i, arr) => (
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    key={`submitted claims - ${item.payment_month}`}
                                                    className="grid grid-cols-4 px-8 py-0 gap-x-2 rounded-none items-start justify-start !text-caption text-text text-left font-normal hover:bg-[#E8D7EE]"
                                                    onClick={() =>
                                                        handleMonthClicked(
                                                            "denied_claims",
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ],
                                                            item.denied_claims_amount,
                                                            item.denied_claims_count
                                                        )
                                                    }
                                                >
                                                    <span className="capitalize">
                                                        {
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ]
                                                        }
                                                    </span>
                                                    <span>
                                                        {showMoneyInAppFormat(
                                                            item.denied_claims_amount
                                                        )}
                                                    </span>
                                                    <span>
                                                        {
                                                            item.denied_claims_count
                                                        }
                                                    </span>
                                                    <span className="flex items-center justify-start gap-x-1.5">
                                                        {arr[i]
                                                            ?.denied_claims_amount ===
                                                            arr[i + 1]
                                                                ?.denied_claims_amount &&
                                                            ""}
                                                        {arr[i]
                                                            ?.denied_claims_amount >
                                                            arr[i + 1]
                                                                ?.denied_claims_amount && (
                                                            <>
                                                                <TrendingUpIcon className="stroke-textLink" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.denied_claims_amount,
                                                                    arr[i]
                                                                        ?.denied_claims_amount
                                                                )}
                                                            </>
                                                        )}
                                                        {arr[i]
                                                            ?.denied_claims_amount <
                                                            arr[i + 1]
                                                                ?.denied_claims_amount && (
                                                            <>
                                                                <TrendingDownIcon className="text-danger" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.denied_claims_amount,
                                                                    arr[i]
                                                                        ?.denied_claims_amount
                                                                )}
                                                            </>
                                                        )}
                                                    </span>
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </>
                            }
                        />
                        <BillingSummaryCard
                            bgColor="var(--bg-pr100-collected)"
                            title="PR100 Collected"
                            tooltipDescription="Collected shows PR100 payments that have been successfully collected from the Patient."
                            description="Total PR100 Collected"
                            count={data.data.summary.pr100_collected_count || 0}
                            smallCount={
                                data.data.summary.pr100_collected
                                    ? showMoneyInAppFormat(
                                          data.data.summary.pr100_collected
                                      )
                                    : "--"
                            }
                            hide={typeFilter === "coinsurance"}
                            showList={doesBreakdownListHaveData}
                            list={
                                <>
                                    <div className="rounded-md bg-[#EED7D7] py-6 px-8 grid grid-cols-4 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                        <span className="capitalize">
                                            Month
                                        </span>
                                        <span>Total amount</span>
                                        <span>Total PR100 collected</span>
                                        <span>Indicator</span>
                                    </div>

                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {handleGetMonthlyBreakdown()?.map(
                                            (item, i, arr) => (
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    key={`collected - ${item.payment_month}`}
                                                    className="grid grid-cols-4 px-8 py-0 gap-x-2 rounded-none items-start justify-start !text-caption text-text text-left font-normal hover:bg-[#EED7D7]"
                                                    onClick={() =>
                                                        handleMonthClicked(
                                                            "pr100_collected",
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ],
                                                            item.pr100_collected,
                                                            item.pr100_collected_count
                                                        )
                                                    }
                                                >
                                                    <span className="capitalize">
                                                        {
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ]
                                                        }
                                                    </span>
                                                    <span>
                                                        {showMoneyInAppFormat(
                                                            item.pr100_collected
                                                        )}
                                                    </span>
                                                    <span>
                                                        {
                                                            item.pr100_collected_count
                                                        }
                                                    </span>
                                                    <span className="flex items-center justify-start gap-x-1.5">
                                                        {arr[i]
                                                            ?.pr100_collected ===
                                                            arr[i + 1]
                                                                ?.pr100_collected &&
                                                            ""}
                                                        {arr[i]
                                                            ?.pr100_collected >
                                                            arr[i + 1]
                                                                ?.pr100_collected && (
                                                            <>
                                                                <TrendingUpIcon className="stroke-textLink" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.pr100_collected,
                                                                    arr[i]
                                                                        ?.pr100_collected
                                                                )}
                                                            </>
                                                        )}
                                                        {arr[i]
                                                            ?.pr100_collected <
                                                            arr[i + 1]
                                                                ?.pr100_collected && (
                                                            <>
                                                                <TrendingDownIcon className="stroke-danger" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.pr100_collected,
                                                                    arr[i]
                                                                        ?.pr100_collected
                                                                )}
                                                            </>
                                                        )}
                                                    </span>
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </>
                            }
                        />
                        <BillingSummaryCard
                            bgColor="var(--bg-interest-collected)"
                            title="Interest Collected"
                            tooltipDescription="This shows the interest payments that have been successfully collected from the Insurance."
                            description="Total Interest Collected"
                            count={data.data.summary.interest_collected_count}
                            smallCount={showMoneyInAppFormat(
                                data.data.summary.interest_collected
                            )}
                            hide={typeFilter === "coinsurance"}
                            showList={doesBreakdownListHaveData}
                            list={
                                <>
                                    <div className="rounded-md bg-[#cde1f9] py-6 px-8 grid grid-cols-4 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                        <span className="capitalize">
                                            Month
                                        </span>
                                        <span>Total amount</span>
                                        <span>Total interest collected</span>
                                        <span>Indicator</span>
                                    </div>
                                    <div className="flex flex-col gap-y-[--spacing-items]">
                                        {handleGetMonthlyBreakdown()?.map(
                                            (item, i, arr) => (
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    key={`interest_collected - ${item.payment_month}`}
                                                    className="grid grid-cols-4 px-8 py-0 gap-x-2 rounded-none items-start justify-start !text-caption text-text text-left font-normal hover:bg-[#cde1f9]"
                                                    onClick={() =>
                                                        handleMonthClicked(
                                                            "interest_collected",
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ],
                                                            item.interest_collected,
                                                            item.interest_collected_count
                                                        )
                                                    }
                                                >
                                                    <span className="capitalize text-left">
                                                        {
                                                            billingPaymentMonths[
                                                                item.payment_month -
                                                                    1
                                                            ]
                                                        }
                                                    </span>
                                                    <span>
                                                        {showMoneyInAppFormat(
                                                            item.interest_collected
                                                        )}
                                                    </span>
                                                    <span>
                                                        {
                                                            item.interest_collected_count
                                                        }
                                                    </span>
                                                    <span className="flex items-center justify-start gap-x-1.5">
                                                        {arr[i]
                                                            ?.interest_collected ===
                                                            arr[i + 1]
                                                                ?.interest_collected &&
                                                            ""}
                                                        {arr[i]
                                                            ?.interest_collected >
                                                            arr[i + 1]
                                                                ?.interest_collected && (
                                                            <>
                                                                <TrendingUpIcon className="stroke-textLink" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.interest_collected,
                                                                    arr[i]
                                                                        ?.interest_collected
                                                                )}
                                                            </>
                                                        )}
                                                        {arr[i]
                                                            ?.interest_collected <
                                                            arr[i + 1]
                                                                ?.interest_collected && (
                                                            <>
                                                                <TrendingDownIcon className="stroke-danger" />
                                                                {getPercentageDifference(
                                                                    arr[i + 1]
                                                                        ?.interest_collected,
                                                                    arr[i]
                                                                        ?.interest_collected
                                                                )}
                                                            </>
                                                        )}
                                                    </span>
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </>
                            }
                        />
                    </div>
                )}
            </Card>
        </>
    );
}
